import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import { DemoPromptsMenu } from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

export function AIExplorerPromptsAndProjectsPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-explorer.prompts-and-projects" })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList data={list("learning_objectives.objectives")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.intro"))}</p>
        <CourseList data={list("overview.tools")} />
        <p>{parse(t("overview.conclusion"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("prompts.title")}</h2>
        <h3>{t("prompts.where.title")}</h3>
        <CourseList data={list("prompts.where.location")} />
        <div className="my-4 flex items-center gap-3">
          <DemoPromptsMenu />
          <span className="text-muted-foreground text-sm">{t("prompts.title")}</span>
        </div>

        <h3>{t("prompts.what_you_can_do.title")}</h3>
        <CourseList data={list("prompts.what_you_can_do.actions")} />

        <h3>{t("prompts.use_cases.title")}</h3>
        {list<{ title: string; points: string[] }>("prompts.use_cases.cases").map(
          (useCase, index) => (
            <div key={index}>
              <h4>{useCase.title}</h4>
              <CourseList data={ensureArray<string>(useCase.points)} />
            </div>
          )
        )}
      </CourseSection>

      <CourseSection>
        <h2>{t("projects.title")}</h2>
        <h3>{t("projects.where.title")}</h3>
        <CourseList data={list("projects.where.location")} />
        {/* Project panel requires complex state - keeping as static description */}

        <h3>{t("projects.what_it_does.title")}</h3>
        <p>{parse(t("projects.what_it_does.intro"))}</p>
        <CourseList data={list("projects.what_it_does.collects")} />
        <p>{t("projects.what_it_does.when_active")}</p>
        <CourseList data={list("projects.what_it_does.benefits")} />

        <h3>{t("projects.use_cases.title")}</h3>
        {list<{ title: string; points: string[] }>("projects.use_cases.cases").map(
          (useCase, index) => (
            <div key={index}>
              <h4>{useCase.title}</h4>
              <CourseList data={ensureArray<string>(useCase.points)} />
            </div>
          )
        )}
      </CourseSection>

      <CourseSection>
        <h2>{t("combination.title")}</h2>
        <p>{parse(t("combination.intro"))}</p>
        <CourseList data={list("combination.benefits")} />

        <h3>{t("combination.example.title")}</h3>
        <CourseList data={list("combination.example.steps")} />
        <p>
          <strong>{t("combination.example.result_label")}</strong>
        </p>
        <p>{parse(t("combination.example.result"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("templates.title")}</h2>
        <p>{parse(t("templates.intro"))}</p>
        {list<{ title: string; content: string }>("templates.examples").map((template, index) => (
          <div key={index}>
            <h3>{template.title}</h3>
            <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
              <p>{parse(template.content)}</p>
            </blockquote>
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("exercise.title")}</h2>
        <p>{parse(t("exercise.intro"))}</p>
        <CourseList data={list("exercise.steps")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={list("summary.points")} />
      </CourseSection>
    </CourseLayout>
  )
}
